import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ATTACHMENT_PROBLEM_MESSAGE, MAX_ATTACHMENT_BYTES, checkAttachment } from '@veta-desk/domain';
import {
  api, ApiError, downloadAttachment, uploadAttachment, type AttachmentView, type Message, type Thread
} from '../lib/api';
import { dateTime, relativeTime } from '../lib/format';

// Conversación del ticket, al estilo Freshdesk / Zendesk:
//  · "Responder al cliente" (blanco) y "Nota interna" (ámbar, nunca la ve el cliente)
//  · adjuntos por mensaje, con marca de sensible decidida ANTES de subir el archivo

type Kind = Message['kind'];

interface Pending { key: string; file: File; sensitive: boolean; problem: string | null }

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1).replace(/\.0$/, "")} MB`;
}

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);
const ClipIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="m21 11-8.5 8.5a5 5 0 0 1-7-7L14 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L15 7" />
  </svg>
);

function AttachmentChip({ number, a, onToggle }: { number: string; a: AttachmentView; onToggle: (a: AttachmentView) => void }) {
  const [error, setError] = useState('');
  if (a.purged) {
    return (
      <li className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-line px-2.5 py-1.5 text-xs text-muted">
        <LockIcon /> Archivo sensible eliminado por retención
      </li>
    );
  }
  if (a.locked) {
    return (
      <li title="Solo lo ven el agente asignado, los líderes y el administrador"
        className="inline-flex items-center gap-1.5 rounded-md border border-line bg-[#F4F3F0] px-2.5 py-1.5 text-xs text-muted">
        <LockIcon /> Archivo sensible · {formatBytes(a.size)}
      </li>
    );
  }
  return (
    <li className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${a.sensitive ? 'border-[#E9C9A8] bg-[#FDF5EC]' : 'border-line bg-white'}`}>
      <button type="button" className="inline-flex items-center gap-1.5 font-medium text-ink hover:underline"
        onClick={() => { setError(''); downloadAttachment(number, a).catch(e => setError(e instanceof ApiError ? e.message : 'No se pudo descargar')); }}>
        {a.sensitive ? <span className="text-[#9A3412]"><LockIcon /></span> : <ClipIcon />}
        {a.fileName}
      </button>
      <span className="text-muted">{formatBytes(a.size)}</span>
      {a.sensitive && <span className="text-[#9A3412]">sensible</span>}
      {a.canMarkSensitive && (
        <button type="button" className="text-muted underline-offset-2 hover:text-ink hover:underline" onClick={() => onToggle(a)}>
          {a.sensitive ? 'quitar marca' : 'marcar sensible'}
        </button>
      )}
      {error && <span role="alert" className="text-[#8F1D1D]">{error}</span>}
    </li>
  );
}

const KIND_STYLE: Record<Message['kind'], { box: string; chip: string; label: string }> = {
  note: { box: 'border-[#F0DFAE] bg-[#FFF9E8]', chip: 'bg-[#F6E7B8] text-[#6A4A00]', label: 'Nota interna' },
  reply: { box: 'border-line bg-white', chip: 'bg-accent-soft text-accent-dark', label: 'Respuesta al cliente' },
  // Lo que escribió el cliente: alineado al otro lado, como un chat, en gris azulado
  inbound: { box: 'border-[#D8E1EA] bg-[#F3F6F9]', chip: 'bg-[#DCE6F0] text-[#28445E]', label: 'Cliente · por correo' }
};

function DeliveryLine({ d }: { d: NonNullable<Message['delivery']> }) {
  if (d.status === 'sent') {
    return <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted">✓ Enviado por correo a {d.to.join(', ')}</p>;
  }
  if (d.status === 'failed') {
    return <p className="mt-2 text-xs text-[#8F1D1D]">No se pudo enviar por correo{d.error ? `: ${d.error}` : ''}. Queda registrada en el ticket.</p>;
  }
  return <p className="mt-2 text-xs text-muted">Registrada sin envío por correo{d.error ? ` (${d.error.toLowerCase()})` : ''}.</p>;
}

function MessageItem({ m, number, files, onToggle }: {
  m: Message; number: string; files: AttachmentView[]; onToggle: (a: AttachmentView) => void
}) {
  const style = KIND_STYLE[m.kind] ?? KIND_STYLE.reply;
  return (
    <li className={`rounded-lg border p-4 ${style.box} ${m.kind === 'inbound' ? 'mr-8' : ''}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
        <strong className="font-semibold">{m.author.name}</strong>
        {m.kind === 'inbound' && m.fromEmail && m.fromEmail !== m.author.name && <span className="text-xs text-muted">&lt;{m.fromEmail}&gt;</span>}
        <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${style.chip}`}>{style.label}</span>
        <span className="text-xs text-muted" title={dateTime(m.createdAt)}>{relativeTime(m.createdAt)}</span>
      </div>
      <p className="mt-2 whitespace-pre-line text-sm leading-relaxed">{m.body}</p>
      {files.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {files.map(a => <AttachmentChip key={a.id} number={number} a={a} onToggle={onToggle} />)}
        </ul>
      )}
      {m.kind === 'reply' && m.delivery && <DeliveryLine d={m.delivery} />}
    </li>
  );
}

export function Conversation({ number, open }: { number: string; open: boolean }) {
  const queryClient = useQueryClient();
  const thread = useQuery({ queryKey: ['thread', number], queryFn: () => api<Thread>(`/tickets/${number}/messages`), refetchInterval: 60_000 });
  const [kind, setKind] = useState<Kind>('reply');
  const [body, setBody] = useState('');
  const [pending, setPending] = useState<Pending[]>([]);
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  // Si el mensaje falla después de subir, al reintentar no se vuelven a subir los mismos archivos.
  const uploadedIds = useRef(new Map<string, string>());

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['thread', number] });
    void queryClient.invalidateQueries({ queryKey: ['ticket', number] });
  };

  const send = useMutation({
    mutationFn: async () => {
      const uploaded: string[] = [];
      for (const p of pending) {
        let id = uploadedIds.current.get(p.key);
        if (!id) {
          id = (await uploadAttachment(number, p.file, p.sensitive)).id;
          uploadedIds.current.set(p.key, id);
        }
        uploaded.push(id);
      }
      return api(`/tickets/${number}/messages`, { method: 'POST', body: JSON.stringify({ kind, body, attachmentIds: uploaded }) });
    },
    onSuccess: () => { setBody(''); setPending([]); setError(''); uploadedIds.current.clear(); refresh(); },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'No se pudo enviar. Inténtalo de nuevo.')
  });

  const toggle = useMutation({
    mutationFn: (a: AttachmentView) => api(`/tickets/${number}/attachments/${a.id}`, { method: 'PATCH', body: JSON.stringify({ sensitive: !a.sensitive }) }),
    onSuccess: refresh,
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'No se pudo cambiar la marca.')
  });

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next = [...list].map(file => {
      const problem = checkAttachment(file.name, file.size);
      return { key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`, file, sensitive: false, problem: problem ? ATTACHMENT_PROBLEM_MESSAGE[problem] : null };
    });
    setPending(p => [...p, ...next]);
    if (fileInput.current) fileInput.current.value = '';
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim() || pending.some(p => p.problem)) return;
    send.mutate();
  };

  const data = thread.data;
  const byMessage = (id: string) => data?.attachments.filter(a => a.messageId === id) ?? [];
  const orphan = data?.attachments.filter(a => !a.messageId) ?? [];
  const note = kind === 'note';

  return (
    <section className="rounded-lg border border-line bg-white p-4" aria-labelledby="conv-title">
      <h2 id="conv-title" className="mb-3 text-sm font-semibold">Conversación</h2>

      {thread.isLoading && <p role="status" className="text-sm text-muted">Cargando…</p>}
      {thread.isError && (
        <p role="alert" className="mb-4 rounded-md bg-[#FBEAEA] px-3 py-2 text-sm text-[#8F1D1D]">
          No se pudo cargar la conversación{thread.error instanceof ApiError ? `: ${thread.error.message}` : ''}.{' '}
          <button type="button" className="underline" onClick={() => void thread.refetch()}>Reintentar</button>
        </p>
      )}
      {data && data.messages.length === 0 && (
        <p className="mb-4 text-sm text-muted">Todavía no hay mensajes. Registra aquí lo que respondes al cliente y las notas internas del equipo.</p>
      )}
      {data && data.messages.length > 0 && (
        <ol className="mb-4 flex flex-col gap-3">
          {data.messages.map(m => <MessageItem key={m.id} m={m} number={number} files={byMessage(m.id)} onToggle={a => toggle.mutate(a)} />)}
        </ol>
      )}
      {orphan.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-xs text-muted">Archivos del ticket</p>
          <ul className="flex flex-wrap gap-2">{orphan.map(a => <AttachmentChip key={a.id} number={number} a={a} onToggle={x => toggle.mutate(x)} />)}</ul>
        </div>
      )}

      <form onSubmit={submit} className={`rounded-lg border ${note ? 'border-[#F0DFAE] bg-[#FFFCF2]' : 'border-line bg-[#FAFAF8]'}`}>
        <div role="tablist" aria-label="Tipo de mensaje" className="flex gap-1 border-b border-line px-2 pt-2">
          {(['reply', 'note'] as Kind[]).map(k => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)}
              className={`rounded-t-md px-3 py-1.5 text-sm ${kind === k
                ? (k === 'note' ? 'bg-[#FFF9E8] font-semibold text-[#6A4A00]' : 'bg-white font-semibold text-ink')
                : 'text-muted hover:text-ink'}`}>
              {k === 'reply' ? 'Responder al cliente' : 'Nota interna'}
            </button>
          ))}
        </div>
        <div className="p-3">
          <label htmlFor="conv-body" className="sr-only">{note ? 'Nota interna' : 'Respuesta al cliente'}</label>
          <textarea id="conv-body" rows={4} value={body} onChange={e => setBody(e.target.value)}
            placeholder={note ? 'Solo la ve el equipo de Ascendix: hallazgos, siguientes pasos, contexto técnico…' : 'Lo que le comunicas al cliente…'}
            className="w-full resize-y rounded-md border border-[#D6D3CD] bg-white px-3 py-2 text-sm leading-relaxed focus:border-accent focus:outline-none" />
          <p className="mt-1 text-xs text-muted">
            {note ? 'El cliente nunca ve las notas internas.'
              : data?.mail?.connected && data.mail.to
                ? `Se enviará por correo a ${data.mail.to}. Los archivos sensibles no se envían.`
                : data?.mail?.connected
                  ? 'El ticket no tiene correo del solicitante: quedará registrada sin envío.'
                  : 'Queda registrada en el ticket (el buzón de soporte aún no está conectado).'}
          </p>

          {pending.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1.5">
              {pending.map(p => (
                <li key={p.key} className={`flex flex-wrap items-center gap-3 rounded-md border px-2.5 py-1.5 text-xs ${p.problem ? 'border-[#E8B4B4] bg-[#FBEAEA]' : 'border-line bg-white'}`}>
                  <span className="inline-flex items-center gap-1.5 font-medium"><ClipIcon />{p.file.name}</span>
                  <span className="text-muted">{formatBytes(p.file.size)}</span>
                  {p.problem ? <span className="text-[#8F1D1D]">{p.problem}</span> : (
                    <label className="inline-flex items-center gap-1.5 text-[#3F3D39]" title="Recibos, layouts con salarios, datos personales">
                      <input type="checkbox" checked={p.sensitive}
                        onChange={e => setPending(list => list.map(x => x.key === p.key ? { ...x, sensitive: e.target.checked } : x))} />
                      Sensible
                    </label>
                  )}
                  <button type="button" className="ml-auto text-muted hover:text-ink" aria-label={`Quitar ${p.file.name}`}
                    onClick={() => setPending(list => list.filter(x => x.key !== p.key))}>Quitar</button>
                </li>
              ))}
              {pending.some(p => p.sensitive) && (
                <li className="text-xs text-[#9A3412]">Los archivos sensibles solo los ven el agente asignado, los líderes y el administrador, y se borran 180 días después de cerrar el ticket.</li>
              )}
            </ul>
          )}

          {error && <p role="alert" className="mt-2 rounded-md bg-[#FBEAEA] px-3 py-2 text-xs text-[#8F1D1D]">{error}</p>}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="submit" disabled={send.isPending || !body.trim() || pending.some(p => p.problem)}
              className="inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50">
              {send.isPending ? 'Enviando…' : note ? 'Agregar nota' : data?.mail?.connected && data.mail.to ? 'Enviar respuesta' : 'Registrar respuesta'}
            </button>
            {data?.attachmentsEnabled && open && (
              <>
                <input ref={fileInput} id="conv-files" type="file" multiple className="sr-only" onChange={e => addFiles(e.target.files)} />
                <label htmlFor="conv-files"
                  className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-[#F4F3F0]">
                  <ClipIcon /> Adjuntar
                </label>
                <span className="text-xs text-muted">hasta {formatBytes(MAX_ATTACHMENT_BYTES)} por archivo</span>
              </>
            )}
            {data && !data.attachmentsEnabled && open && (
              <span className="text-xs text-muted">Los adjuntos no están activos en este servidor (falta conectar el bucket).</span>
            )}
          </div>
        </div>
      </form>
    </section>
  );
}
